import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Modal as NModal,
  type ModalProps as Props,
  tv,
} from '@heroui/react';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { IoWarningOutline } from 'react-icons/io5';
import { MdOutlineInfo } from 'react-icons/md';
import { VscError } from 'react-icons/vsc';
import { Button } from '../..';

const information = tv({
  slots: {
    iconWrapper: 'p-2 bg-danger-50 rounded-full flex justify-center items-center',
    titleClass: 'text-default-800',
    messageClass: 'text-default-500',
    listClass: 'p-4 list-inside list-disc',
  },
  variants: {
    type: {
      warn: {
        iconWrapper: 'bg-warning-100',
        successButton: 'bg-warning-400',
        successText: 'text-content1',
      },
      success: {
        iconWrapper: 'bg-success-50',
        successButton: 'bg-success-500',
        successText: 'text-content1',
      },
      error: { iconWrapper: 'bg-danger-50', successButton: 'bg-danger-500', successText: 'text-white' },
      info: {
        iconWrapper: 'bg-sky-200 dark:bg-sky-900',
        successButton: 'bg-sky-500 dark:bg-sky-900',
        successText: 'text-content1 dark:text-white',
      },
    },
    size: {
      sm: { titleClass: 'text-sm', messageClass: 'text-sm' },
      md: { titleClass: 'text-lg', messageClass: 'text-base' },
      lg: { titleClass: 'text-xl', messageClass: 'text-lg' },
    },
  },
});

// type ConfirmVariants = VariantProps<typeof confirm>;
type InformationVariants = {
  type?: 'warn' | 'success' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
};

export interface InformationProps extends Omit<Props, 'children' | 'size'>, InformationVariants {
  title: string;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  children?: Props['children'];
  list?: string[];
  message?: string;
}

export const Information = ({
  title,
  message,
  list = [],
  isOpen,
  size = 'md',
  type = 'warn',
  onOpenChange,
  children,
  ...props
}: InformationProps) => {
  const { iconWrapper, messageClass, listClass, titleClass } = information();

  const mapping = {
    warn: <IoWarningOutline className='text-warning-700 w-6 h-6' />,
    success: <FaRegCircleCheck className='text-success-700 w-6 h-6' />,
    error: <VscError className='text-danger-700 w-6 h-6' />,
    info: <MdOutlineInfo className='text-sky-700 dark:text-sky-200 w-6 h-6' />,
  };

  return (
    <NModal
      backdrop='blur'
      radius='sm'
      hideCloseButton={true}
      className='max-w-2xl'
      {...props}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className='flex flex-row space-x-4 border-b border-divider items-center'>
              <span className={iconWrapper({ type })}>{mapping[type]}</span>
              <p className={titleClass({ size })}>{title}</p>
            </ModalHeader>
            <ModalBody>
              {children ? (
                children
              ) : (
                <ul className={listClass()}>
                  {list.map((i, idx) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: <disabled>
                      key={idx}
                      className={messageClass({ size })}
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: <disabled>
                      dangerouslySetInnerHTML={{ __html: i }}
                    />
                  ))}
                </ul>
              )}
            </ModalBody>
            <ModalFooter className=''>
              <Button radius='sm' color='default' variant='bordered' onPress={onClose}>
                Okay
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </NModal>
  );
};
